/*
 * The sold-out panel.
 *
 * Takes an email address and hands it to Klaviyo's public back-in-stock
 * endpoint, so the shop can write when the variant is in stock again. Posting
 * from here rather than letting Klaviyo's own widget draw a pop-up is what
 * keeps the moment inside the page's design.
 *
 * Two things it deliberately does not do. It does not work out for itself
 * whether the variant can be bought: Dawn re-renders the product section on
 * every variant change, and the panel lifts its own freshly rendered contents
 * out of that, so the wording and the sold-out judgement stay in Liquid. And it
 * does not remember an address beyond the visit; the subscriptions it has
 * already made are held per variant on the element, so switching to a finish
 * you have already asked about shows that, and a reload starts clean.
 */
if (!customElements.get('back-in-stock')) {
  customElements.define(
    'back-in-stock',
    class BackInStock extends HTMLElement {
      // Pinned rather than tracking the latest: Klaviyo promises a request
      // shape only for the revision it is sent with.
      static API_REVISION = '2026-07-15';

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.companyId = this.dataset.companyId;

        // Which variants this visitor has already subscribed to, and with which
        // address, so switching away and back shows what they did.
        this.subscribed = new Map();

        this.addEventListener('submit', this.onSubmit);
        this.addEventListener('click', this.onClick);
        this.listenForVariantChange();
      }

      disconnectedCallback() {
        if (this.variantSubscriber) this.variantSubscriber();
      }

      get inner() {
        return this.querySelector(`#BackInStockInner-${this.sectionId}`);
      }

      get variantId() {
        return this.inner?.dataset.variantId;
      }

      onClick = (event) => {
        if (!event.target.closest('.back-in-stock__undo')) return;

        this.subscribed.delete(this.variantId);
        this.showForm();
        this.querySelector('.back-in-stock__form input')?.focus();
      };

      onSubmit = (event) => {
        event.preventDefault();

        const form = event.target.closest('.back-in-stock__form');
        const input = form?.querySelector('input[type="email"]');
        if (!input) return;

        const email = input.value.trim();
        if (!input.checkValidity() || email === '') {
          this.showError(this.dataset.invalidEmail);
          input.focus();
          return;
        }

        this.subscribe(email, form);
      };

      subscribe(email, form) {
        const variantId = this.variantId;
        const button = form.querySelector('.back-in-stock__submit');

        this.showError('');
        button?.classList.add('loading');
        button?.querySelector('.loading__spinner')?.classList.remove('hidden');
        if (button) button.disabled = true;

        fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(this.companyId)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: this.constructor.API_REVISION,
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email } } },
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    // How Klaviyo names a variant it has synced from Shopify.
                    id: `$shopify:::$default:::${variantId}`,
                  },
                },
              },
            },
          }),
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Klaviyo answered ${response.status}`);
            this.subscribed.set(variantId, email);
            this.showDone(email);
          })
          .catch((error) => {
            console.error(error);
            this.showError(this.dataset.error);
          })
          .finally(() => {
            button?.classList.remove('loading');
            button?.querySelector('.loading__spinner')?.classList.add('hidden');
            if (button) button.disabled = false;
          });
      }

      showDone(email) {
        const done = this.querySelector('.back-in-stock__done');
        const form = this.querySelector('.back-in-stock__form');
        const fine = this.querySelector('.back-in-stock__fine');
        if (!done) return;

        const slot = done.querySelector('.back-in-stock__email');
        if (slot) slot.textContent = email;

        form?.setAttribute('hidden', '');
        fine?.setAttribute('hidden', '');
        this.showError('');
        done.removeAttribute('hidden');
      }

      showForm() {
        this.querySelector('.back-in-stock__done')?.setAttribute('hidden', '');
        this.querySelector('.back-in-stock__form')?.removeAttribute('hidden');
        this.querySelector('.back-in-stock__fine')?.removeAttribute('hidden');
      }

      showError(message) {
        const error = this.querySelector('.back-in-stock__error');
        if (!error) return;

        error.textContent = message || '';
        error.toggleAttribute('hidden', !message);
      }

      /*
       * A freshly chosen variant brings its own panel with it, already worded
       * and already knowing whether it is sold out. Swapping the contents in
       * wholesale means nothing here has to decide any of that again.
       */
      listenForVariantChange() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantSubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.sectionId) return;

          const source = data.html.getElementById(`BackInStockInner-${this.sectionId}`);
          const destination = this.inner;
          if (!source || !destination) return;

          destination.innerHTML = source.innerHTML;
          destination.dataset.variantId = source.dataset.variantId;

          const already = this.subscribed.get(source.dataset.variantId);
          if (already) this.showDone(already);
        });
      }
    }
  );
}
